import type { IndexCollectionItem } from "@/repo";
import ControlPanelListSearch from "./ControlPanelListSearch";
import { useEffect, useMemo, useState, type RefObject } from "react";
import "./ControlPanelList.css";
import type { Map } from "mapbox-gl";
import classNames from "classnames";
import { createMapboxFillAndOpacity } from "@/constants";
import { useNavigate, useParams } from "react-router-dom";
import { getEnvParam, type TileEnv } from "@/env";

interface LocationCollectionListProps {
    collections: IndexCollectionItem[];
    map: RefObject<Map | undefined>;
    env: TileEnv;
}

function LocationCollectionList(props: LocationCollectionListProps) {
    const { id } = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const [search, setSearch] = useState("");

    const selectedId = id ? Number(id) : undefined;

    useEffect(() => {
        if (selectedId === undefined) return;
        const map = props.map.current;
        if (!map) return;

        if (map.getSource("mvt-tiles")) {
            if (map.getLayer("tiles")) map.removeLayer("tiles");
            if (map.getLayer("tiles-values")) map.removeLayer("tiles-values");
            map.removeSource("mvt-tiles");
        }

        map.addSource("mvt-tiles", {
            type: "vector",
            tiles: [
                `${process.env.BUN_PUBLIC_TILE_SERVER}/api/tiles/collections/${selectedId}/{z}/{x}/{y}?${getEnvParam()}`,
            ],
        });

        map.addLayer({
            id: "tiles",
            type: "fill",
            source: "mvt-tiles",
            "source-layer": "values",
            paint: createMapboxFillAndOpacity(["get", "value_normalized"]),
        });

        map.addLayer({
            id: "tiles-values",
            type: "symbol",
            source: "mvt-tiles",
            "source-layer": "values",
            minzoom: 12.5,
            paint: {
                "text-color": "#fff",
                "text-opacity": [
                    "interpolate",
                    ["linear"],
                    ["get", "value_normalized"],
                    0,
                    0.4,
                    1,
                    0.6,
                ],
            },
            layout: {
                "text-field": ["get", "value"],
                "text-font": ["Source Code Pro", "Arial Unicode MS Bold"],
                "text-offset": [0, 0],
                "text-size": 8,
                "text-anchor": "center",
            },
        });

        return () => {
            const m = props.map.current;
            if (!m) return;
            if (m.getLayer("tiles-values")) m.removeLayer("tiles-values");
            if (m.getLayer("tiles")) m.removeLayer("tiles");
            if (m.getSource("mvt-tiles")) m.removeSource("mvt-tiles");
        };
    }, [selectedId, props.map, props.env]);

    const collections = useMemo(() => {
        return props.collections.filter((collection) =>
            collection.name
                .toLowerCase()
                .trim()
                .includes(search.toLowerCase().trim()),
        );
    }, [props.collections, search]);

    return (
        <>
            <ControlPanelListSearch
                type="collection"
                search={search}
                setSearch={setSearch}
            />
            <div className="control-panel-button-list">
                {collections.map((collection) => (
                    <button
                        key={collection.id}
                        onClick={() => {
                            setSearch("");
                            navigate(`/location/collections/${collection.id}`);
                        }}
                        className={classNames(
                            "control-panel-button-list__item",
                            collection.id === selectedId &&
                                "control-panel-button-list__item--active",
                        )}
                    >
                        <div></div>
                        {collection.name}
                    </button>
                ))}
            </div>
        </>
    );
}

export default LocationCollectionList;
